import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ILessonHistory,
  TLessonHistoryClassExtensionRequestStatus,
  TLessonHistoryTutorAmountData,
  TLessonHistoryTutorPaymentConcernStatus,
  TLessonHistoryTutorPaymentStatus,
} from '.';
import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import { AdminApiPaths } from '../../../api/APIPaths';
import { TLessonTypes, TMeetingSectionPaymentType } from '../SectionSlice';

export type TAdminLessonHistoryListGet = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  startDate?: string;
  endDate?: string;
  studentId?: string;
  tutorId?: string;
  courseId?: string;
  referenceKey?: string;
  curriculum?: string;
  grade?: string;
  subject?: string;
  lessonType?: TLessonTypes;
  paymentType?: TMeetingSectionPaymentType;
  tutorPaymentStatus?: TLessonHistoryTutorPaymentStatus;
  includeTutorAmountData?: boolean;
};

export type TAdminGetLessonHistoryById = {
  id: string;
};

export type TAdminCreateLessonHistoryAdminNote = {
  id: string;
  description: string;
};

export type TAdminDeleteLessonHistoryAdminNote = {
  id: string;
  noteId: string;
};

export type TAdminUpdateLessonHistoryTutorPaymentStatus = {
  lesssonHistoryIds: string[];
  tutorPaymentStatus: TLessonHistoryTutorPaymentStatus;
};

export type TAdminUpdateLessonHistoryTutorPaymentAdjustments = {
  lessonHistoryId: string;
  tutorRate: number;
  tutorRateCurrency: string;
  duration: number;
};

export type TAdminRespondToLessonHistoryTutorPaymentConcern = {
  lessonHistoryId: string;
  status: TLessonHistoryTutorPaymentConcernStatus;
  adminNote: string;
};

export type TAdminUpdateLessonHistoryClassExtension = {
  lessonHistoryId: string;
  lessonExtensionMinutes: number;
  classExtensionRequestStatus?: TLessonHistoryClassExtensionRequestStatus;
};

export type TAdminUpdateLessonHistoryTutorPenalty = {
  lessonHistoryId: string;
  penalty: number;
  penaltyReason?: string;
};

export type TAdminUpdateLessonHistoryTrialConversion = {
  lessonHistoryId: string;
  isTrialConverted: boolean;
  trialStatus: string;
};

const apiDataProvider = new APIDataProvider();

export const adminLessonHistoryListGet = createAsyncThunk(
  '/admin/lesson-history/list',
  async ({
    limit,
    offset,
    sortKey,
    sortValue,
    startDate,
    endDate,
    studentId,
    tutorId,
    courseId,
    referenceKey,
    curriculum,
    grade,
    subject,
    lessonType,
    paymentType,
    tutorPaymentStatus,
    includeTutorAmountData,
  }: TAdminLessonHistoryListGet) => {
    let path = `${AdminApiPaths.lessonHistory}?limit=${limit}&offset=${offset}`;

    if (sortKey) {
      path += `&sortKey=${sortKey}`;
    }

    if (sortValue) {
      path += `&sortValue=${sortValue}`;
    }

    if (startDate) {
      path += `&startDate=${startDate}`;
    }

    if (endDate) {
      path += `&endDate=${endDate}`;
    }

    if (studentId) {
      path += `&studentId=${studentId}`;
    }

    if (tutorId) {
      path += `&tutorId=${tutorId}`;
    }

    if (courseId) {
      path += `&courseId=${courseId}`;
    }

    if (referenceKey) {
      path += `&referenceKey=${referenceKey}`;
    }

    if (curriculum) {
      path += `&curriculum=${curriculum}`;
    }

    if (grade) {
      path += `&grade=${grade}`;
    }

    if (subject) {
      path += `&subject=${subject}`;
    }

    if (lessonType) {
      path += `&lessonType=${lessonType}`;
    }

    if (paymentType) {
      path += `&paymentType=${paymentType}`;
    }

    if (tutorPaymentStatus) {
      path += `&tutorPaymentStatus=${tutorPaymentStatus}`;
    }

    if (includeTutorAmountData) {
      path += `&includeTutorAmountData=1`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<{
        offset: number;
        limit: number;
        totalDocs: number;
        docs: Array<ILessonHistory>;
        tutorAmountData?: TLessonHistoryTutorAmountData;
      }>
    >(path);

    return response.data;
  }
);

export const adminGetLessonHistoryById = createAsyncThunk(
  '/admin/lesson-history/getOne',
  async ({ id }: TAdminGetLessonHistoryById) => {
    const path = `${AdminApiPaths.lessonHistory}/${id}`;

    const response = await apiDataProvider.get<TApiResponse<ILessonHistory>>(
      path
    );

    return response.data;
  }
);

export const adminCreateLessonHistoryAdminNote = createAsyncThunk(
  '/admin/lesson-history/admin-notes/create',
  async (data: TAdminCreateLessonHistoryAdminNote) => {
    const response = await apiDataProvider.post<TApiResponse<ILessonHistory>>(
      `${AdminApiPaths.lessonHistory}/admin-notes/${data.id}`,
      data
    );

    return response.data;
  }
);

export const adminDeleteLessonHistoryAdminNote = createAsyncThunk(
  '/admin/lesson-history/admin-notes/delete',
  async ({ id, noteId }: TAdminDeleteLessonHistoryAdminNote) => {
    const response = await apiDataProvider.delete<TApiResponse<ILessonHistory>>(
      `${AdminApiPaths.lessonHistory}/admin-notes/${id}?id=${id}&noteId=${noteId}`
    );

    return response.data;
  }
);

export const adminUpdateLessonHistoryTutorPaymentStatus = createAsyncThunk(
  '/admin/lesson-history/update-tutor-payment-status',
  async (data: TAdminUpdateLessonHistoryTutorPaymentStatus) => {
    const response = await apiDataProvider.put<
      TApiResponse<TAdminUpdateLessonHistoryTutorPaymentStatus>
    >(`${AdminApiPaths.lessonHistory}/update-tutor-payment-status`, data);

    return response.data;
  }
);

export const adminUpdateLessonHistoryTutorPaymentAdjustments = createAsyncThunk(
  '/admin/lesson-history/tutor-payments/adjustments',
  async (data: TAdminUpdateLessonHistoryTutorPaymentAdjustments) => {
    const response = await apiDataProvider.put<TApiResponse<ILessonHistory>>(
      `${AdminApiPaths.lessonHistory}/tutor-payments/adjustments`,
      data
    );

    return response.data;
  }
);

export const adminRespondToLessonHistoryTutorPaymentConcern = createAsyncThunk(
  '/admin/lesson-history/tutor-payments/respond-to-concern',
  async (data: TAdminRespondToLessonHistoryTutorPaymentConcern) => {
    const response = await apiDataProvider.put<TApiResponse<ILessonHistory>>(
      `${AdminApiPaths.lessonHistory}/tutor-payments/respond-to-concern`,
      data
    );

    return response.data;
  }
);

export const adminUpdateLessonHistoryClassExtension = createAsyncThunk(
  '/admin/lesson-history/tutor-payments/class-extension',
  async (data: TAdminUpdateLessonHistoryClassExtension) => {
    const response = await apiDataProvider.put<TApiResponse<ILessonHistory>>(
      `${AdminApiPaths.lessonHistory}/tutor-payments/class-extension`,
      data
    );

    return response.data;
  }
);

export const adminUpdateLessonHistoryTutorPenalty = createAsyncThunk(
  '/admin/lesson-history/tutor-payments/tutor-penalty',
  async (data: TAdminUpdateLessonHistoryTutorPenalty) => {
    const response = await apiDataProvider.put<TApiResponse<ILessonHistory>>(
      `${AdminApiPaths.lessonHistory}/tutor-payments/tutor-penalty`,
      data
    );

    return response.data;
  }
);

export const adminUpdateLessonHistoryTrialConversion = createAsyncThunk(
  '/admin/lesson-history/tutor-payments/trial-conversion',
  async (data: TAdminUpdateLessonHistoryTrialConversion) => {
    const response = await apiDataProvider.put<TApiResponse<ILessonHistory>>(
      `${AdminApiPaths.lessonHistory}/tutor-payments/trial-conversion`,
      data
    );

    return response.data;
  }
);
